import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Alternative export to avoid naming conflicts
export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSystemTheme, setIsSystemTheme] = useState(true);

  // Initialize theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || savedTheme === 'light') {
      // User has manually set a preference
      setIsDarkMode(savedTheme === 'dark');
      setIsSystemTheme(false);
    } else {
      // Use system preference as default
      setIsDarkMode(prefersDark);
      setIsSystemTheme(true);
    }
  }, []);

  // Apply theme to document (hh-brand, global-theme, Tailwind/shadcn all read these classes)
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark-mode', 'dark');
      root.classList.remove('light-mode');
    } else {
      root.classList.remove('dark-mode', 'dark');
      root.classList.add('light-mode');
    }
  }, [isDarkMode]);

  // Listen for system theme changes
  useEffect(() => {
    if (isSystemTheme) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        setIsDarkMode(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [isSystemTheme]);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    setIsSystemTheme(false);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const setTheme = (theme) => {
    setIsDarkMode(theme === 'dark');
    setIsSystemTheme(false);
    localStorage.setItem('theme', theme);
  };

  const useSystemTheme = () => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(prefersDark);
    setIsSystemTheme(true);
    localStorage.removeItem('theme');
  };

  const value = {
    isDarkMode,
    isSystemTheme,
    toggleTheme,
    setTheme,
    useSystemTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
